import React from 'react';
import { FormattedMessage } from 'react-intl';

import { useIdelinkTheme } from '../../../configs/IdeLinkTheme';
import { ItemState } from '../../../services/commons/ItemState';
import { AdminPermission } from '../../../services/commons/Me';
import { AdminPermissions, AdminPermissionsOnModule, } from '../../../services/commons/Permissions/AdminPermissions';
import {
    ContentPermission,
    UserPermissions,
    UserPermissionsOnContent,
} from '../../../services/commons/Permissions/ContentPermission';
import { PermissionsAbilityHelper } from '../../../services/commons/Permissions/PermissionsAbilityHelper';
import { PermissionType } from '../../../services/commons/Permissions/PermissionType';
import { getApiPermissionName } from '../../../configs/ApiPermissionsMap';
import { TabPermissionItem } from '../../BackUsers/Pages/components/PermissionsTreeContainer/PermissionsTreeContainer';

export interface AdminPermissionsOnOffer extends AdminPermissionsOnModule {
    canSynchronize?: boolean;
}

export interface UserPermissionsOnOffer extends UserPermissionsOnContent {
    unitPriceEnabled?: boolean;
    remainingCountEnabled?: boolean;
    deliveryWeekEnabled?: boolean;

    //* PRODUCTS
    canAddProduct?: boolean;
    addProductEnabled?: boolean;
    saveProductEnabled?: boolean;
    canImportProducts?: boolean;
    importProductsEnabled?: boolean;
    removeProductEnabled?: boolean;

    productTitleEnabled?: boolean;
    productExternalReferenceEnabled?: boolean;
    productDescriptionEnabled?: boolean;
    productImage?: {
        canAdd?: boolean;
        canReplace?: boolean;
        canRemove?: boolean;
    };

    productQuantityEnabled?: boolean;
    productMarginRateEnabled?: boolean;
    productPromoMarginRateEnabled?: boolean;
    productPurchasePriceEnabled?: boolean;
    productPromoPurchasePriceEnabled?: boolean;
    productNormalBoughtPriceEnabled?: boolean;
    productNormalSellPriceEnabled?: boolean;
    productPromoBoughtPriceEnabled?: boolean;
    productPromoSellPriceEnabled?: boolean;

    promoODRPriceEnabled?: boolean; //! FLUIZY
}

export class OffersPermissions {
    public static OFFERS_TAB_HASH = '#rights-offers';
    public static OFFERS_KEY = 'OFFERS_KEY';
    public static OFFERS_NAME = 'PackagedOrders';

    public static PackagedOrders: AdminPermissionsOnOffer = {};
    public static TunableOffers: AdminPermissionsOnOffer = {};

    public static getOffersTab = (): TabPermissionItem => ({
        hash: OffersPermissions.OFFERS_TAB_HASH,
        label: (
            <FormattedMessage id="backofficeUsers.create.tabs.permissions.sectionTabs.packagedOrders" />
        ),
        getIcon: () => useIdelinkTheme().offers.permissionIcon,
        getIconSelected: () => useIdelinkTheme().offers.permissionSelectedIcon,
    });

    public static initializeOffersAdminPermissions = (perms: Array<AdminPermission> = []) => {
        const moduleName = getApiPermissionName(OffersPermissions.OFFERS_KEY);
        const permsOnOffers = AdminPermissions.filter(perms, moduleName);

        const toAdminPermissionsOnOffers = () => {
            const adminPermissions: AdminPermissionsOnOffer = {
                ...AdminPermissions.toPermissions(permsOnOffers),
                canSynchronize: AdminPermissions.has(
                    PermissionType.ContentSynchronization,
                    permsOnOffers
                ),
            };
            return adminPermissions;
        };

        OffersPermissions.PackagedOrders = toAdminPermissionsOnOffers();
        PermissionsAbilityHelper.logPermissions(moduleName, OffersPermissions.PackagedOrders);

        OffersPermissions.TunableOffers = toAdminPermissionsOnOffers();
        PermissionsAbilityHelper.logPermissions('TunableOffers', OffersPermissions.TunableOffers);
    };

    public static toDefaultOfferPermisisons = (): UserPermissionsOnOffer => {
        const rawPermissions: UserPermissionsOnOffer = OffersPermissions.toUserPermissionsOnOffer(
            ItemState.Draft
        );
        const permissions: { [field: string]: boolean } = {};
        Object.keys(rawPermissions).forEach((key) => (permissions[key] = true));
        return permissions as UserPermissionsOnOffer;
    };

    public static toUserPermissionsOnOffer = (
        contentState: ItemState,
        contentPermission?: ContentPermission
    ): UserPermissionsOnOffer => {
        const canEdit = UserPermissions.canEdit(contentState, contentPermission);
        const isDraft =
            contentState === ItemState.Draft &&
            UserPermissions.has(PermissionType.ContentEditDraft, contentPermission);

        const isFinished =
            contentState === ItemState.Finished &&
            UserPermissions.has(PermissionType.ContentEditPublished, contentPermission);

        const isPublishedOrUnpublished =
            (contentState === ItemState.Published &&
                UserPermissions.has(PermissionType.ContentEditDraft, contentPermission)) ||
            (contentState === ItemState.Unpublished &&
                UserPermissions.has(PermissionType.ContentEditDraft, contentPermission));

        return {
            ...UserPermissions.toUserPermissionsOnContent(contentState, contentPermission),
            canEditPdf: canEdit,
            canSave: isDraft || isFinished || isPublishedOrUnpublished,
            canSendManualNotification: isFinished || isPublishedOrUnpublished,

            unitPriceEnabled: isDraft,
            remainingCountEnabled: isDraft || isPublishedOrUnpublished,
            deliveryWeekEnabled: isDraft || isFinished || isPublishedOrUnpublished,

            //* PRODUCTS
            canAddProduct: isDraft,
            addProductEnabled: !isPublishedOrUnpublished,
            canImportProducts: isDraft,
            importProductsEnabled: !isPublishedOrUnpublished,
            removeProductEnabled: isDraft,
            saveProductEnabled: canEdit,

            productTitleEnabled: canEdit,
            productDescriptionEnabled: canEdit,
            productExternalReferenceEnabled: isDraft,
            productImage: {
                canAdd: canEdit,
                canRemove: canEdit,
                canReplace: canEdit,
            },
            productQuantityEnabled: isDraft,
            productMarginRateEnabled: canEdit,
            productPromoMarginRateEnabled: canEdit,
            productPurchasePriceEnabled: canEdit,
            productPromoPurchasePriceEnabled: isDraft,
            productNormalBoughtPriceEnabled: canEdit,
            productNormalSellPriceEnabled: canEdit,
            productPromoBoughtPriceEnabled: canEdit,
            productPromoSellPriceEnabled: canEdit,
            promoODRPriceEnabled: canEdit,
        };
    };
}
